import { useReadinessStore } from '@/stores/readinessStore'
import { useMigrationStore } from '@/stores/migrationStore'
import { useNotificationStore, followKey } from '@/stores/notificationStore'
import { scopeKey } from '@/lib/readiness/scope'
import { liveLevel } from '@/lib/readiness/score'
import { levelFromNum, levelNum } from '@/api/types/readiness'
import type { CapabilityChange } from '@/stores/catalogueStore'
import type { CatalogueUsage } from '@/api/types/catalogue'
import { CAPABILITY_LABEL } from '@/api/types/catalogue'
import { monthEnd, monthLabel, objectivesForTechnology } from '@/lib/catalogue/engine'

// ── Catalogue write fan-out (Iteration 3 §11.2, Note 5) ─────────────────────
// A capability change (status or roadmap date) updates `target` on items with
// `target_source: catalogue`, writes `dependency_changed` on every affected
// objective, and enqueues notifications to the objective owner, the owners of
// the affected items and the objective's followers, collapsed per 24 h.

export interface FanoutContext {
  orgId: string
  indexId: string
  usage: CatalogueUsage[]
  deadline: string | null // CSA submission date
}

export function fanOutCapabilityChange(change: CapabilityChange, c: FanoutContext): { codes: string[]; moved: number } {
  const { technology, capability, before, after } = change
  const assetIds = c.usage.filter((u) => u.technology_id === technology.id).map((u) => u.asset_id)
  const mStore = useMigrationStore.getState()
  const mscope = mStore.scopes[scopeKey(c.orgId, c.indexId)] ?? null

  // 1. Items whose target follows the catalogue move with the vendor date.
  const moved = after.roadmap_date !== before?.roadmap_date ? mStore.applyCatalogueDate(c.orgId, c.indexId, { asset_ids: assetIds, capability, roadmap_date: after.roadmap_date }) : []

  // 2. dependency_changed on each objective whose scope includes the technology.
  const codes = objectivesForTechnology(technology.id, useMigrationStore.getState().scopes[scopeKey(c.orgId, c.indexId)] ?? mscope, c.usage)
  if (!codes.length) return { codes, moved: moved.length }
  const ready = after.status === 'pqc_ready' && before?.status !== 'pqc_ready'
  const kind = ready ? 'dependency_ready' : 'vendor_date_moved'
  const text = ready
    ? `Dependency ready · ${technology.name} shipped PQC for ${CAPABILITY_LABEL[capability].toLowerCase()}`
    : `Vendor date moved · ${technology.name} · ${monthLabel(before?.roadmap_date)} → ${monthLabel(after.roadmap_date)} · from catalogue`
  const events = useReadinessStore.getState().recordDependencyChange(c.orgId, c.indexId, {
    codes,
    text,
    dependency: { kind, technology_id: technology.id, technology: technology.name, capability, old: before?.roadmap_date ?? null, new: after.roadmap_date, asset: null },
  })

  // 3. Notifications: only a change that moves a date on a gated step (or
  //    readies it) notifies; routine re-syncs never do (§13.1).
  const rScope = useReadinessStore.getState().scopes[scopeKey(c.orgId, c.indexId)]
  const nStore = useNotificationStore.getState()
  const followers = (code: string) => Object.entries(nStore.follows).filter(([k, v]) => k.startsWith(`${c.orgId}::${c.indexId}::`) && v.includes(code)).map(([k]) => k.slice(followKey(c.orgId, c.indexId, '').length))
  const pastDeadline = !!after.roadmap_date && !!c.deadline && monthEnd(after.roadmap_date) > c.deadline
  const pastPinned = !!after.roadmap_date && (mscope?.items ?? []).some((i) => i.target_source === 'owner' && i.target_date && monthEnd(after.roadmap_date!) > i.target_date && mscope!.item_assets.some((x) => x.item_id === i.id && assetIds.includes(x.asset_id)))
  events.forEach((ev) => {
    const code = ev.objective_code!
    const st = rScope?.states[code]
    const next = levelFromNum(Math.min(3, levelNum(liveLevel(st)) + 1))
    const itemOwners = (mscope?.items ?? []).filter((i) => i.step_links?.some((l) => l.objective_code === code) && mscope!.item_assets.some((x) => x.item_id === i.id && assetIds.includes(x.asset_id))).map((i) => i.owner_user_id ?? '')
    const recipients = [st?.owner ?? '', ...itemOwners, ...followers(code)]
    nStore.notify(
      {
        org_id: c.orgId,
        index_id: c.indexId,
        kind: 'dependency_date_moved',
        objective_code: code,
        event_id: ev.id,
        technology_id: technology.id,
        consequence: ready ? `${code} step unblocked · ${technology.name} is PQC-ready` : `Earliest ${next} for ${code} moved to ${monthLabel(after.roadmap_date)}`,
        cause: ready ? `${CAPABILITY_LABEL[capability]} now PQC-ready` : `${technology.name} roadmap changed`,
        raises_alert: pastDeadline || pastPinned,
        bypass_digest: pastDeadline,
      },
      recipients,
      ev.occurred_at
    )
  })
  return { codes, moved: moved.length }
}
